import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api/api';
import AdminLayout from './AdminLayout';

const AdminDashboard = () => {
    const [stats, setStats] = useState({ projects: 0, sections: 0, skills: 0, services: 0, totalVisitors: 0, uniqueVisitors: 0 });
    const [groupedVisits, setGroupedVisits] = useState([]);
    const [expandedIp, setExpandedIp] = useState(null);
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    { data: projects },
                    { data: sections },
                    { data: skills },
                    { data: services },
                    { data: visitorData }
                ] = await Promise.all([
                    api.fetchProjects(),
                    api.fetchSections(),
                    api.fetchSkills(),
                    api.fetchServices(),
                    api.fetchVisitorStats()
                ]);

                setStats({ 
                    projects: projects.length, 
                    sections: sections.length, 
                    skills: skills.length, 
                    services: services.length,
                    totalVisitors: visitorData.count || 0,
                    uniqueVisitors: visitorData.uniqueCount || 0
                });
                setGroupedVisits(visitorData.groupedVisits || []);
            } catch (error) {
                // Fail silently to optimize user experience
            }
        };
        fetchData();
    }, []);

    return (
        <AdminLayout title="System Overview">
            <div className="mb-8 flex justify-between items-end">
                <div>
                    <h2 className="text-3xl font-extrabold text-white tracking-tight">
                        Dashboard
                    </h2>
                    <p className="text-gray-400 mt-1">
                        Welcome back, <span className="text-indigo-400 font-semibold">{userInfo?.name || 'Admin'}</span>. Here's what's happening.
                    </p>
                </div>
                <div className="hidden sm:block">
                    <Link to="/" target="_blank" className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg shadow-lg hover:shadow-indigo-500/30 transition-all">
                        <i className="fas fa-external-link-alt mr-2"></i>
                        Live Preview
                    </Link>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-10">
                {/* Projects Card */}
                <Link to="/admin/projects" className="relative group overflow-hidden rounded-2xl bg-gray-800 p-6 border border-gray-700 hover:border-indigo-500 transition-all shadow-xl">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-indigo-500/20 rounded-xl">
                            <i className="fas fa-briefcase text-indigo-400 text-xl"></i>
                        </div>
                        <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Portfolio</span>
                    </div>
                    <div>
                        <p className="text-3xl font-bold text-white mb-1">{stats.projects}</p>
                        <p className="text-sm text-gray-400">Total Projects</p>
                    </div>
                </Link>

                {/* Skills Card */}
                <Link to="/admin/skills" className="relative group overflow-hidden rounded-2xl bg-gray-800 p-6 border border-gray-700 hover:border-purple-500 transition-all shadow-xl">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-500/20 rounded-xl">
                            <i className="fas fa-tools text-purple-400 text-xl"></i>
                        </div>
                        <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Expertise</span>
                    </div>
                    <div>
                        <p className="text-3xl font-bold text-white mb-1">{stats.skills}</p>
                        <p className="text-sm text-gray-400">Technical Skills</p>
                    </div>
                </Link>

                {/* Services Card */}
                <Link to="/admin/services" className="relative group overflow-hidden rounded-2xl bg-gray-800 p-6 border border-gray-700 hover:border-cyan-500 transition-all shadow-xl">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-cyan-500/20 rounded-xl">
                            <i className="fas fa-concierge-bell text-cyan-400 text-xl"></i>
                        </div>
                        <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">Services</span>
                    </div>
                    <div>
                        <p className="text-3xl font-bold text-white mb-1">{stats.services}</p>
                        <p className="text-sm text-gray-400">Active Services</p>
                    </div>
                </Link>

                {/* Hero Page Card */}
                <Link to="/admin/hero" className="relative group overflow-hidden rounded-2xl bg-gray-800 p-6 border border-gray-700 hover:border-pink-500 transition-all shadow-xl">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-pink-500/10 rounded-full blur-2xl group-hover:bg-pink-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-pink-500/20 rounded-xl">
                            <i className="fas fa-magic text-pink-400 text-xl"></i>
                        </div>
                        <span className="text-xs font-bold text-pink-400 uppercase tracking-wider">System</span>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-white mb-1 uppercase tracking-tight">Dynamic</p>
                        <p className="text-sm text-gray-400">Hero Page Content</p>
                    </div>
                </Link>

                {/* Unique Visitors Card */}
                <div 
                    className="relative group overflow-hidden rounded-2xl bg-gray-800 p-6 border border-gray-700 hover:border-yellow-500 transition-all shadow-xl cursor-pointer col-span-1 sm:col-span-2 lg:col-span-2"
                    onClick={() => document.getElementById('visitors-table').scrollIntoView({ behavior: 'smooth' })}
                >
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl group-hover:bg-yellow-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-yellow-500/20 rounded-xl">
                            <i className="fas fa-users text-yellow-400 text-xl"></i>
                        </div>
                        <div className="text-right">
                             <div className="text-xs font-bold text-yellow-500 uppercase tracking-wider">Traffic Analysis</div>
                             <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{stats.totalVisitors} TOTAL HITS</div>
                        </div>
                    </div>
                    <div className="flex items-end justify-between">
                        <div>
                            <p className="text-4xl font-black text-white mb-1">{stats.uniqueVisitors.toLocaleString()}</p>
                            <p className="text-sm text-gray-400">Unique Visitors Identified</p>
                        </div>
                        <div className="text-yellow-500 bg-yellow-500/10 px-3 py-1 rounded-lg text-xs font-bold animate-pulse">
                            LIVE TRACKING
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
                <h3 className="text-xl font-bold text-white">Quick Tasks</h3>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-700 to-transparent"></div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7 mb-10">
                <Link to="/admin/projects/add" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-plus text-indigo-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Add Project</span>
                </Link>

                <Link to="/admin/hero" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-edit text-pink-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Hero Page</span>
                </Link>

                <Link to="/admin/services" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-concierge-bell text-cyan-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Services</span>
                </Link>

                <Link to="/admin/skills" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-tools text-purple-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Skills</span>
                </Link>

                <Link to="/admin/sections" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-layer-group text-green-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Sections</span>
                </Link>

                <Link to="/admin/resume" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-file-alt text-yellow-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">Resume</span>
                </Link>

                <Link to="/" target="_blank" className="group p-4 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 transition-all text-center flex flex-col items-center">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <i className="fas fa-external-link-alt text-blue-400 text-lg"></i>
                    </div>
                    <span className="text-xs font-bold text-gray-300 uppercase tracking-tighter group-hover:text-white">View Site</span>
                </Link>
            </div>

            {/* Recent Visitors Table - Grouped by IP */}
            <div className="mt-10" id="visitors-table">
                <h3 className="text-base font-semibold leading-6 text-gray-200 mb-4">
                    <i className="fas fa-eye mr-2 text-cyan-400"></i>
                    Visitors <span className="text-gray-500 text-sm font-normal ml-1">({groupedVisits.length} unique IPs)</span>
                </h3>
                <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800 shadow">
                    {groupedVisits.length === 0 ? (
                        <div className="px-6 py-10 text-center text-gray-400">
                            <i className="fas fa-user-slash text-3xl mb-3 block text-gray-600"></i>
                            No visitors yet. Share your portfolio!
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-700/50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-8"></th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">#</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP Address</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Pages Visited</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedVisits.map((visitor, index) => {
                                    const isOpen = expandedIp === visitor.ip;
                                    const lastTime = new Date(visitor.lastVisit);
                                    const now = new Date();
                                    const diffMs = now - lastTime;
                                    const diffMins = Math.floor(diffMs / 60000);
                                    const diffHrs = Math.floor(diffMins / 60);
                                    const diffDays = Math.floor(diffHrs / 24);
                                    let timeAgo;
                                    if (diffMins < 1) timeAgo = 'Just now';
                                    else if (diffMins < 60) timeAgo = `${diffMins}m ago`;
                                    else if (diffHrs < 24) timeAgo = `${diffHrs}h ago`;
                                    else timeAgo = `${diffDays}d ago`;

                                    return (
                                        <React.Fragment key={visitor.ip}>
                                            <tr
                                                className={`cursor-pointer transition-colors border-t border-gray-700 ${isOpen ? 'bg-gray-700/60' : 'hover:bg-gray-700/30'}`}
                                                onClick={() => setExpandedIp(isOpen ? null : visitor.ip)}
                                            >
                                                <td className="px-4 py-4 text-center">
                                                    <i className={`fas fa-chevron-right text-gray-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                                                        style={{ display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                                                    ></i>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-500">{index + 1}</td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center">
                                                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/20 mr-3 flex-shrink-0">
                                                            <i className="fas fa-user text-cyan-400 text-xs"></i>
                                                        </span>
                                                        <span className="text-sm font-mono text-gray-200">{visitor.ip}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-900/40 text-cyan-300 border border-cyan-700">
                                                        <i className="fas fa-file-alt mr-1 text-xs"></i>
                                                        {visitor.totalPages} page{visitor.totalPages !== 1 ? 's' : ''}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-400">
                                                    <i className="fas fa-clock mr-1 text-xs text-gray-500"></i>
                                                    {timeAgo}
                                                </td>
                                            </tr>

                                            {isOpen && (
                                                <tr className="bg-gray-900/50 border-t border-gray-700/50">
                                                    <td colSpan={5} className="px-0 py-0">
                                                        <div className="px-6 py-3">
                                                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-medium">
                                                                Pages visited by {visitor.ip}
                                                            </p>
                                                            <div className="space-y-1">
                                                                {visitor.pages.map((p, pi) => {
                                                                    const pt = new Date(p.visitedAt);
                                                                    return (
                                                                        <div key={pi} className="flex items-center justify-between py-1.5 px-3 rounded-md bg-gray-800/70 border border-gray-700/50">
                                                                            <div className="flex items-center gap-3">
                                                                                <span className="text-gray-600 text-xs w-4 text-right">{pi + 1}.</span>
                                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/50 font-mono">
                                                                                    {p.page || '/'}
                                                                                </span>
                                                                            </div>
                                                                            <span className="text-xs text-gray-500" title={pt.toLocaleString('en-IN')}>
                                                                                <i className="fas fa-clock mr-1 text-gray-600"></i>
                                                                                {pt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
